import {get, post, patch, del} from './http';

export type TaxonomyMatch = {
  concept_id: string;
  name: string;
  // SKOS mapping kind: 'exact', 'broad', 'narrow' or 'close'.
  kind: string;
};

export type ClassificationConfig = {
  id: string;
  name: string;
  display_name: string;
  type: 'policy_violation' | 'vertical' | 'risk';
  parent_id: string | null;
  category: 'Actor' | 'Content' | null;
  restricted: boolean;
  // Risk-only - flags this risk category for the P0 escalation-timer flow. Must not be set on a
  // vertical (the backend rejects it).
  is_potential_p0: boolean;
  // Safeguarding: once an item is classified under this entry or anything beneath it, every image
  // stored for it is deleted and its thumbnail is no longer shown. Irreversible per item.
  purge_imagery: boolean;
  categories: Array<'Actor' | 'Content'>;
  hidden_facets: Array<'Actor' | 'Content'>;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // How this entry maps onto the resolver's taxonomy, as the pack declared it. A customer's tree
  // is their own, so an entry may map onto several of our concepts, one, or none — and an entry an
  // analyst created by hand has none. Optional because only a pack carrying mappings supplies it.
  matches?: TaxonomyMatch[];
};

export type CreateVerticalRequest = {
  name: string;
  display_name: string;
  restricted: boolean;
  sort_order?: number;
};

export type CreateRiskRequest = {
  name: string;
  display_name: string;
  parent_id: string;
  sort_order?: number;
};

export type CreateClassificationConfigRequest = {
  name: string;
  display_name: string;
  parent_id?: string | null;
  category?: 'Actor' | 'Content' | null;
  restricted?: boolean;
  is_potential_p0?: boolean;
  purge_imagery?: boolean;
  sort_order?: number;
};

export type UpdateVerticalRequest = {
  name?: string;
  display_name?: string;
  restricted?: boolean;
  sort_order?: number;
};

export type UpdateRiskRequest = {
  name?: string;
  display_name?: string;
  sort_order?: number;
};

export type UpdateClassificationConfigRequest = {
  name?: string;
  display_name?: string;
  category?: 'Actor' | 'Content';
  restricted?: boolean;
  is_potential_p0?: boolean;
  purge_imagery?: boolean;
  sort_order?: number;
};

export const classificationConfigsApi = {
  list: async () => get<ClassificationConfig[]>('/admin/taxonomy-configs'),

  get: async (id: string) => get<ClassificationConfig>(`/admin/taxonomy-configs/${id}`),

  // Generic methods (preferred for multi-level support)
  create: async (body: CreateClassificationConfigRequest) =>
    post<CreateClassificationConfigRequest, ClassificationConfig>('/admin/taxonomy-configs', body),

  update: async (id: string, body: UpdateClassificationConfigRequest) =>
    patch<UpdateClassificationConfigRequest, ClassificationConfig>(
      `/admin/taxonomy-configs/${id}`,
      body
    ),

  setHidden: async (id: string, facet: 'Actor' | 'Content', hidden: boolean) =>
    patch<{facet: 'Actor' | 'Content'; hidden: boolean}, ClassificationConfig[]>(
      `/admin/taxonomy-configs/${id}/visibility`,
      {facet, hidden}
    ),

  // Legacy methods (deprecated - kept for backward compatibility)
  createVertical: async (body: CreateVerticalRequest) =>
    post<CreateVerticalRequest, ClassificationConfig>(
      '/admin/taxonomy-configs/policy-violations',
      body
    ),

  createRisk: async (body: CreateRiskRequest) =>
    post<CreateRiskRequest, ClassificationConfig>('/admin/taxonomy-configs/risks', body),

  updateVertical: async (id: string, body: UpdateVerticalRequest) =>
    patch<UpdateVerticalRequest, ClassificationConfig>(
      `/admin/taxonomy-configs/policy-violations/${id}`,
      body
    ),

  updateRisk: async (id: string, body: UpdateRiskRequest) =>
    patch<UpdateRiskRequest, ClassificationConfig>(`/admin/taxonomy-configs/risks/${id}`, body),

  delete: async (id: string) => del(`/admin/taxonomy-configs/${id}`)
};
